import React, { ReactNode } from 'react';
import Accordion from '@mui/material/Accordion';
import { SxProps, useTheme, Theme } from '@mui/material/styles';
import AccordionSummary from '@mui/material/AccordionSummary';
import ChevronRight from '@mui/icons-material/ChevronRight';
import AccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';

const getBackgroundColor = (color: 'greenGray' | 'none', theme: Theme) => {
  if (color === 'none') return 'transparent';
  return theme.palette[color].main;
};

const AccordionLevelContext = React.createContext(0);

export const AccordionRow = ({
  children,
}: {
  children: ReactNode;
}) => {
  const theme = useTheme();
  const nestedLevel = React.useContext(AccordionLevelContext);
  return (
    <Box
      display="flex"
      flexDirection="column"
      paddingLeft={8.5 + (nestedLevel - 1) * 2}
      paddingRight={2}
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
          borderBottom: 0,
        },
      }}
    >
      {children}
    </Box>
  );
};

const AccordionV2 = ({
  title,
  children,
  bgColorSummary = 'none',
  sxSummary,
  sxDetails,
}: {
  title: ReactNode;
  children: ReactNode;
  bgColorSummary?: 'greenGray' | 'none';
  sxSummary?: SxProps;
  sxDetails?: SxProps;
}) => {
  const theme = useTheme();
  const nestedLevel = React.useContext(AccordionLevelContext);
  return (
    <Accordion
      disableGutters
      sx={{
        '&:before': {
          display: 'none',
        },
        '& .MuiAccordionSummary-root': {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        '&:last-child:not(.Mui-expanded) .MuiAccordionSummary-root': {
          borderBottom: 0,
        },
        '&.Mui-expanded': {
          borderBottom: `1px solid ${theme.palette.divider}`,
          '&:last-child': {
            borderBottom: 0,
          },
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronRight />}
        sx={{
          backgroundColor: getBackgroundColor(bgColorSummary, theme),
          flexDirection: 'row-reverse',
          gap: 1,
          '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
            transform: 'rotate(90deg)',
          },
          ...(nestedLevel > 0 && {
            paddingX: 2 + nestedLevel * 2,
          }),
          ...sxSummary,
        }}
      >
        {title}
      </AccordionSummary>
      <AccordionLevelContext.Provider value={nestedLevel + 1}>
        <AccordionDetails sx={{ padding: 0, ...sxDetails }}>
          {children}
        </AccordionDetails>
      </AccordionLevelContext.Provider>
    </Accordion>
  );
};

export default AccordionV2;
